// Freshness bucketing for yard spot RTLS reads. Thresholds mirror the
// backend (Shared.Models.RtlsHistory.get_latest_by_yard_spot_grouped_by_zone_for_facility/1)
// so a spot's chip color always agrees with which bucket it's counted in
// on the summary card.

export type FreshnessKey = 'fresh' | 'stale' | 'cold' | 'notSeen';

export interface FreshnessBucket {
  key: FreshnessKey;
  label: string;
  color: string;
  bg: string;
}

const FRESH_MAX_MINUTES = 30;
const STALE_MAX_MINUTES = 120;
const COLD_MAX_MINUTES = 240;

export const FRESHNESS_BUCKETS: FreshnessBucket[] = [
  { key: 'fresh', label: 'Fresh', color: '#2e7d32', bg: '#f2f8f2' },
  { key: 'stale', label: 'Stale', color: '#f9a825', bg: '#fffbea' },
  { key: 'cold', label: 'Cold', color: '#ef6c00', bg: '#fff3e0' },
  { key: 'notSeen', label: 'Not seen', color: '#c62828', bg: '#fdecea' },
];

const BUCKETS_BY_KEY: Record<FreshnessKey, FreshnessBucket> = Object.fromEntries(
  FRESHNESS_BUCKETS.map(bucket => [bucket.key, bucket])
) as Record<FreshnessKey, FreshnessBucket>;

// needsReview is an independent flag (from `verdict`), not a freshness bucket
// — a spot can be fresh and still have a bad read. It takes priority over
// freshness coloring wherever a spot is rendered.
export const NEEDS_REVIEW_COLOR = '#7b1fa2';
export const NEEDS_REVIEW_BG = '#f6effa';

export function ageMinutes(timestamp: string | null | undefined, now: Date): number | null {
  if (!timestamp) return null;
  const readAt = new Date(timestamp);
  if (Number.isNaN(readAt.getTime())) return null;
  return (now.getTime() - readAt.getTime()) / 60000;
}

export function freshnessBucketFor(timestamp: string | null | undefined, now: Date): FreshnessBucket {
  const age = ageMinutes(timestamp, now);
  if (age === null) return BUCKETS_BY_KEY.notSeen;
  if (age <= FRESH_MAX_MINUTES) return BUCKETS_BY_KEY.fresh;
  if (age <= STALE_MAX_MINUTES) return BUCKETS_BY_KEY.stale;
  if (age <= COLD_MAX_MINUTES) return BUCKETS_BY_KEY.cold;
  return BUCKETS_BY_KEY.notSeen;
}

export function formatAge(timestamp: string | null | undefined, now: Date): string {
  const minutes = ageMinutes(timestamp, now);
  if (minutes === null) return 'never';
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins ? `${hours}h ${mins}m` : `${hours}h`;
  }
  const days = Math.floor(minutes / 1440);
  const hours = Math.round((minutes % 1440) / 60);
  return hours ? `${days}d ${hours}h` : `${days}d`;
}
